/* eslint-disable import/order */
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { RegularTextLevel2, RegularTextLevel4 } from '@yme/tokens/typography';
import { Caption } from './Caption';
import { CalendarWrapperStyled } from './DatePickerStyled';
import { Input } from './Input';
import { RangeCalendar } from './RangeCalendar';
import { SingleCalendar } from './SingleCalendar';
import { datePickerClassName, DEFAULT_DROPDOWN_RANGE_FROM, DEFAULT_DROPDOWN_RANGE_TO, DEFAULT_INPUT_PLACEHOLDER_SINGLE, } from './constants';
import { DatePickerMode } from './types';
import { formatCalendarDate } from './helpers';
// !important: md: the order affects the priority of styles
import { DatePickerLibStyled } from './react-day-picker.styled';
import { DatePickerCustomStyled } from './react-day-picker.custom.styled';
import { useWindowSize } from '@yme/common/hooks';
import { BottomSheet, Header } from '@yme/atoms/bottom-sheet';
import { Button, CONTAINED_VARIANT, NO_ICON, SIZE_44 } from '@yme/atoms/button';
import { useTranslation } from '@yme/services/i18n';
import { DatePickerForTimeSlots } from './DatePickerForTimeSlots';
export const DatePicker = memo(({ mode = DatePickerMode.SINGLE, date: incomingDate, inputState, inputPlaceholder, resetInputValue, dropdownRangeFrom = DEFAULT_DROPDOWN_RANGE_FROM, dropdownRangeTo = DEFAULT_DROPDOWN_RANGE_TO, isRightToLeft, disabledDays, onDateChange, onInputBlur, bottomSheetLabel, isBottomSheetNeeded = false, withFullMonthName = false, isInputHasDisabledDays = true, isOpenedInitially = false, isDatePickerForTimeSlots = false, }) => {
    const [isCalendarOpened, setIsCalendarOpened] = useState(isOpenedInitially);
    const [inputValue, setInputValue] = useState('');
    const [date, setDate] = useState(incomingDate);
    const [isBottomSheetExpanded, setIsBottomSheetExpanded] = useState(false);
    const { isInfiniteDesktopView } = useWindowSize();
    const { t } = useTranslation();
    const INPUT_PLACEHOLDER_MAP = {
        [DatePickerMode.SINGLE]: DEFAULT_INPUT_PLACEHOLDER_SINGLE,
        [DatePickerMode.RANGE]: `${t('common_from_label')} - ${t('common_to_label')}`,
    };
    useEffect(() => {
        setDate(incomingDate);
        setInputValue(formatCalendarDate(incomingDate));
    }, [incomingDate]);
    useEffect(() => {
        if (resetInputValue) {
            setInputValue('');
        }
    }, [resetInputValue]);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event?.target?.closest(`.${datePickerClassName}`)) {
                setIsCalendarOpened(false);
            }
            event.stopPropagation();
        };
        window.addEventListener('mousedown', handleClickOutside);
        return () => {
            window.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);
    const handleDateChanged = useCallback((value) => {
        setDate(value);
        onDateChange?.(value);
    }, [onDateChange]);
    const toggleCalendar = useCallback(() => {
        setIsCalendarOpened(!isCalendarOpened);
        setIsBottomSheetExpanded(true);
    }, [isCalendarOpened]);
    const handleCloseBottomSheet = useCallback(() => {
        setIsBottomSheetExpanded(false);
    }, []);
    // !important: md: there is need somehow pass props to Caption component,
    // but we can't do it in regular way since have to pass only component (not element) to library
    // PS: React.cloneElement or React.Context would overhead here
    const CaptionComponent = useMemo(
    // eslint-disable-next-line react/no-unstable-nested-components
    () => function CustomCaption() {
        return (React.createElement(Caption, { isRightToLeft: isRightToLeft, dropdownRangeFrom: dropdownRangeFrom, dropdownRangeTo: dropdownRangeTo, withFullMonthName: withFullMonthName }));
    }, [dropdownRangeFrom, dropdownRangeTo, isRightToLeft, withFullMonthName]);
    const Calendar = useMemo(() => {
        if (mode === DatePickerMode.SINGLE) {
            return (React.createElement(SingleCalendar, { date: date, disabledDays: disabledDays, onChange: handleDateChanged, toggleCalendar: toggleCalendar, CaptionComponent: CaptionComponent, setInputValue: setInputValue }));
        }
        return (React.createElement(RangeCalendar, { date: date, disabledDays: disabledDays, onChange: handleDateChanged, toggleCalendar: toggleCalendar, CaptionComponent: CaptionComponent, setInputValue: setInputValue }));
    }, [CaptionComponent, date, handleDateChanged, mode, toggleCalendar, disabledDays]);
    if (isDatePickerForTimeSlots) {
        return (React.createElement(DatePickerForTimeSlots, { isRightToLeft: isRightToLeft, bottomSheetLabel: bottomSheetLabel, inputValue: inputValue, setInputValue: setInputValue, date: date, onDateChange: handleDateChanged, CaptionComponent: CaptionComponent, disabledDays: disabledDays }));
    }
    return (React.createElement("div", { className: datePickerClassName },
        React.createElement(Input, { value: inputValue, placeholder: inputPlaceholder || INPUT_PLACEHOLDER_MAP[mode], state: inputState, datepickerMode: mode, disabledDays: isInputHasDisabledDays ? disabledDays : undefined, isRightToLeft: isRightToLeft, onIconClick: toggleCalendar, onTextChange: setInputValue, onDateChange: handleDateChanged, onBlur: onInputBlur }),
        isInfiniteDesktopView || !isBottomSheetNeeded ? (isCalendarOpened && (React.createElement(CalendarWrapperStyled, null,
            React.createElement(RegularTextLevel4, null,
                React.createElement(DatePickerLibStyled, null,
                    React.createElement(DatePickerCustomStyled, { mode: mode }, Calendar)))))) : (React.createElement(BottomSheet, { initialFocusRef: false, open: isBottomSheetExpanded, header: React.createElement(Header, { headerLeft: React.createElement(RegularTextLevel2, { weight: 500 }, bottomSheetLabel) }), footer: React.createElement(Button, { size: SIZE_44, hasIconButton: NO_ICON, buttonVariant: CONTAINED_VARIANT, label: t('common_confirm_button'), onClick: handleCloseBottomSheet }), onDismiss: handleCloseBottomSheet },
            React.createElement(RegularTextLevel4, null,
                React.createElement(DatePickerLibStyled, null,
                    React.createElement(DatePickerCustomStyled, { mode: mode, isBottomSheetNeeded: true }, Calendar)))))));
});
DatePicker.displayName = 'DatePicker';
